import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { BeforeAfter } from "@/components/BeforeAfter";
import { HERO_IMAGE } from "@/data/content";
import { Seo } from "@/components/Seo";
import { crumbs } from "@/config/seo";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      <Seo
        title="About MetalMuse — Regina, Saskatchewan Metal Art Studio"
        description="MetalMuse is a Regina studio crafting premium sublimation metal prints — family portraits, pets, memorials, anime and custom art. Every piece is enhanced, printed and inspected by hand."
        path="/about"
        keywords="MetalMuse studio, Regina metal prints, sublimation aluminum art, custom metal photo Saskatchewan"
        jsonLd={[crumbs([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])]}
      />
      <section className="container-lux pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeader eyebrow="About MetalMuse" title="Your Memories. Made to Last." />
          <div className="flex flex-col gap-14">
            {[
              { n: "01", t: "Our Belief", text: "MetalMuse is a Regina studio built on one belief: the photos that matter most deserve better than a phone screen or a fading sheet of paper." },
              { n: "02", t: "The Craft", text: "We take your photos — sent through Messenger, Instagram, WhatsApp or this website — and print them onto premium aluminum panels using high-definition sublimation. The image isn't sitting on the surface; it's infused into it. Waterproof, fade resistant, scratch resistant, alive with colour." },
              { n: "03", t: "The Promise", text: "From family portraits and wedding photos to pet portraits, memorials, car photography and anime metal posters — every piece is enhanced, printed and inspected by hand before it reaches your wall." },
            ].map((c) => (
              <Reveal key={c.n}>
                <div data-testid={`about-chapter-${c.n}`} className="flex gap-7 md:gap-10 items-start">
                  <span className="font-tech text-6xl md:text-7xl leading-none shrink-0 text-transparent" style={{ WebkitTextStroke: "1.5px rgba(0,229,255,0.55)" }}>
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-tech text-2xl md:text-3xl text-[#F5F5F7]">{c.t}</h3>
                    <p className="text-[#A1A1A6] mt-3 leading-relaxed text-base md:text-lg">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <Link to="/order" data-testid="about-order-cta" className="inline-flex items-center gap-2 bg-white text-black font-tech text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:-translate-y-1">
              Order Your Metal Print <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="rounded-2xl overflow-hidden border border-white/10 cyan-glow">
            <img src={HERO_IMAGE} alt="MetalMuse metal print displayed in a premium interior" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="bg-[#0A0A0A] border-t border-white/5 section-spacing">
        <div className="container-lux">
          <SectionHeader eyebrow="Our Craft" title="See what metal does to a photo" text="Drag the slider to compare a regular print with the depth and vibrancy of sublimation on aluminum." />
          <Reveal>
            <BeforeAfter />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
